package org.example.project.engineletter.layouts.sys

import org.example.project.engineletter.layouts.SupportLayoutSysCustomDocumentContainer
import org.example.project.engineletter.layouts.SupportLayoutSysCustomPage

import androidx.compose.runtime.Composable
import org.example.project.utils.All
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Img
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

private data class InvoiceRow(
    val vertragsart: String,
    val menge: String,
    val einzelpreis: String,
    val gesamt: String
)

@Composable
fun SysTextToPdfRechnungslayout(
    rootElementId: String? = null,
    rechnung: All,
    kunde: All,
    unternehmen: All,
    logoSrc: String,
    currentSysUser: All,
    editable: Boolean = false
) {
    val rows = buildInvoiceRows(rechnung)
    val monthLabel = invoiceMonthLabel(rechnung.datum)
    val nettoSumme = parseInvoiceNumber(rechnung.gesamtbetragEuro)
    val umsatzsteuer = nettoSumme * 0.19
    val bruttoSumme = nettoSumme + umsatzsteuer
    val rechnungsNummer = rechnung.rechnungsnummer.ifBlank {
        invoiceNumber(rechnung.firmenId, rechnung.datum)
    }
    val grussformelText = "Mit freundlichen Grüßen"
    val signaturBildSrc = currentSysUser.signatur
    val signaturNameText = listOf(currentSysUser.vorname, currentSysUser.nachname)
        .filter { it.isNotBlank() }
        .joinToString(" ")
    val datumText = invoiceDisplayDate(
        rechnung.rechnungsdatum
            .ifBlank { invoiceDate(rechnung.datum) }
            .ifBlank { rechnung.abgeschlossenAm }
            .ifBlank { rechnung.datum }
    )

    Div({
        rootElementId?.takeIf { it.isNotBlank() }?.let { attr("id", it) }
        if (editable) {
            attr("contenteditable", "true")
            attr("spellcheck", "true")
        }
    }) {
    SupportLayoutSysCustomDocumentContainer(rootElementId = null, sideBySidePreview = false) {
        SupportLayoutSysCustomPage(
            kunde = kunde,
            unternehmen = unternehmen,
            logoSrc = logoSrc,
            betreffText = "Rechnung $monthLabel",
            firmenIdText = "Rechnungsnummer: $rechnungsNummer",
            datumText = "Datum: $datumText",
            pageNumber = 1,
            totalPages = 1
        ) {
            InvoiceIntroPage(
                monthLabel = monthLabel,
                rows = rows,
                summeNetto = invoiceAmount(rechnung.gesamtbetragEuro, nettoSumme),
                umsatzsteuer = invoiceAmount("", umsatzsteuer),
                summeBrutto = invoiceAmount("", bruttoSumme),
                grussformelText = grussformelText,
                signaturBildSrc = signaturBildSrc,
                signaturNameText = signaturNameText
            )
        }
    }
    }
}

@Composable
private fun InvoiceIntroPage(
    monthLabel: String,
    rows: List<InvoiceRow>,
    summeNetto: String,
    umsatzsteuer: String,
    summeBrutto: String,
    grussformelText: String,
    signaturBildSrc: String,
    signaturNameText: String
) {
    Div({
        style {
            flexGrow(1)
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            fontSize(16.px)
            lineHeight(20.px)
        }
    }) {
        Div({ style { marginTop(14.px); marginBottom(20.px) } }) {
            Text("Sehr geehrte Damen und Herren,")
        }

        Div({ style { marginBottom(20.px) } }) {
            Text("hiermit stellen wir Ihnen die Rechnung für $monthLabel zur Verfügung.")
            Div({ style { height(8.px) } })
            Text("Bitte überweisen Sie den Rechnungsbetrag in Höhe von ")
            Span({
                style {
                    property("font-weight", "700")
                }
            }) {
                Text(summeBrutto)
            }
            Text(" innerhalb von 14 Tagen nach Rechnungsdatum unter Angabe der Rechnungsnummer auf das unten genannte Konto.")
        }

        InvoiceTablePage(
            rows = rows,
            summeNetto = summeNetto,
            umsatzsteuer = umsatzsteuer,
            summeBrutto = summeBrutto
        )

        Div({
            style {
                marginTop(18.px)
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
                gap(4.px)
            }
        }) {
            Div { Text(grussformelText) }
            if (signaturBildSrc.startsWith("data:image/")) {
                Img(src = signaturBildSrc, attrs = {
                    style {
                        property("max-width", "220px")
                        property("max-height", "72px")
                        property("object-fit", "contain")
                    }
                })
            } else {
                Div({ style { minHeight(44.px) } }) { Text(" ") }
            }
            Div { Text(signaturNameText) }
        }
    }
}

@Composable
private fun InvoiceTablePage(
    rows: List<InvoiceRow>,
    summeNetto: String,
    umsatzsteuer: String,
    summeBrutto: String
) {
    Div({
        style {
            flexGrow(1)
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            fontSize(16.px)
            lineHeight(20.px)
            marginTop(20.px)
        }
    }) {
        Div({
            style {
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                paddingTop(7.px)
                paddingBottom(7.px)
                property("font-weight", "700")
            }
        }) {
            Div({ style { width(10.percent) } }) { Text("Pos") }
            Div({ style { width(32.percent) } }) { Text("Leistung") }
            Div({ style { width(16.percent); textAlign("center") } }) { Text("Menge") }
            Div({ style { width(20.percent); textAlign("right") } }) { Text("EUR") }
            Div({ style { width(22.percent); textAlign("right") } }) { Text("Gesamt") }
        }

        rows.forEachIndexed { index, row ->
            Div({
                style {
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    paddingTop(3.px)
                    paddingBottom(3.px)
                }
            }) {
                Div({ style { width(10.percent) } }) { Text((index + 1).toString()) }
                Div({ style { width(32.percent) } }) { Text(row.vertragsart) }
                Div({ style { width(16.percent); textAlign("center") } }) { Text(row.menge) }
                Div({ style { width(20.percent); textAlign("right") } }) { Text(row.einzelpreis) }
                Div({ style { width(22.percent); textAlign("right") } }) { Text(row.gesamt) }
            }
        }

        Div({
            style {
                height(1.px)
                width(100.percent)
                backgroundColor(Color("#111111"))
                marginTop(8.px)
            }
        })

        Div({
            style {
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                paddingTop(9.px)
                paddingBottom(3.px)
                property("font-weight", "700")
            }
        }) {
            Div({ style { width(72.percent) } }) { Text("Summe Netto") }
            Div({ style { width(28.percent); textAlign("right") } }) { Text(summeNetto) }
        }

        Div({
            style {
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                paddingTop(1.px)
                paddingBottom(3.px)
            }
        }) {
            Div({ style { width(72.percent) } }) { Text("Umsatzsteuer (19%)") }
            Div({ style { width(28.percent); textAlign("right") } }) { Text(umsatzsteuer) }
        }

        Div({
            style {
                height(1.px)
                width(100.percent)
                backgroundColor(Color("#111111"))
                marginTop(6.px)
            }
        })

        Div({
            style {
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                paddingTop(7.px)
                property("font-weight", "700")
            }
        }) {
            Div({ style { width(72.percent) } }) { Text("Summe Brutto") }
            Div({ style { width(28.percent); textAlign("right") } }) { Text(summeBrutto) }
        }
    }
}

private fun buildInvoiceRows(rechnung: All): List<InvoiceRow> =
    listOf(
        invoiceRow("Onboarding", "1", rechnung.onboardingPreisEinheit, rechnung.onboardingPreis),
        invoiceRow("Lizenzierung", "1", rechnung.lizensierungPreisEinheit, rechnung.lizensierungPreis),
        invoiceRow("CR", invoiceHours(rechnung.crStd), rechnung.crPreisProStd, rechnung.crKosten),
        invoiceRow("Meeting", invoiceHours(rechnung.meetingStd), rechnung.meetingPreisProStd, rechnung.meetingKosten),
        invoiceRow("Schulung", invoiceHours(rechnung.schulungStd), rechnung.schulungPreisProStd, rechnung.schulungKosten),
        invoiceRow("Beratung", invoiceHours(rechnung.beratungStd), rechnung.beratungPreisProStd, rechnung.beratungKosten)
    ).filterNot { it.isZero }

private fun invoiceRow(vertragsart: String, menge: String, rawUnitPrice: String, rawTotal: String): InvoiceRow {
    val totalNumber = parseInvoiceNumber(rawTotal)
    val unitPrice = rawUnitPrice.ifBlank { rawTotal }
    return InvoiceRow(
        vertragsart = vertragsart,
        menge = menge,
        einzelpreis = invoiceAmount(unitPrice, parseInvoiceNumber(unitPrice)),
        gesamt = invoiceAmount(rawTotal, totalNumber)
    )
}

private val InvoiceRow.isZero: Boolean
    get() = parseInvoiceNumber(gesamt) == 0.0

private fun invoiceHours(raw: String): String {
    val hours = parseInvoiceNumber(raw)
    if (hours == 0.0) return "0 Std."
    val formatted = js("new Intl.NumberFormat('de-DE', { minimumFractionDigits: 0, maximumFractionDigits: 2 }).format")(hours) as String
    return "$formatted Std."
}

private fun invoiceAmount(raw: String, fallback: Double): String {
    val amount = if (raw.isBlank()) fallback else parseInvoiceNumber(raw)
    val formatted = js("new Intl.NumberFormat('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format")(amount) as String
    return "$formatted EUR"
}

private fun parseInvoiceNumber(value: String): Double {
    val normalized = value
        .replace("EUR", "", ignoreCase = true)
        .replace("€", "")
        .replace("€", "")
        .replace(".", "")
        .replace(",", ".")
        .trim()
    return normalized.toDoubleOrNull() ?: 0.0
}

private fun invoiceMonthLabel(monthKey: String): String {
    val parts = monthKey.split("_", "-")
    val year = parts.getOrNull(0).orEmpty()
    val month = parts.getOrNull(1)?.toIntOrNull()
    val monthName = listOf(
        "Januar", "Februar", "März", "April", "Mai", "Juni",
        "Juli", "August", "September", "Oktober", "November", "Dezember"
    ).getOrNull((month ?: 1) - 1).orEmpty()
    return listOf(monthName, year).filter { it.isNotBlank() }.joinToString(" ").ifBlank { monthKey }
}

private fun invoiceDisplayDate(value: String): String {
    val trimmed = value.trim()
    return when {
        trimmed.matches(Regex("\\d{4}-\\d{2}-\\d{2}")) -> {
            val parts = trimmed.split("-")
            "${parts[2]}.${parts[1]}.${parts[0]}"
        }
        trimmed.matches(Regex("\\d{4}_\\d{2}")) -> invoiceMonthLabel(trimmed)
        else -> trimmed
    }
}

private fun invoiceNumber(firmenId: String, monthKey: String): String {
    val company = firmenId.filter { it.isLetterOrDigit() }.uppercase()
    val period = monthKey.filter { it.isDigit() }
    return (company + period).ifBlank { "-" }
}

private fun invoiceDate(monthKey: String): String {
    val match = Regex("^(\\d{4})_(\\d{2})$").matchEntire(monthKey.trim()) ?: return ""
    val year = match.groupValues[1].toIntOrNull() ?: return ""
    val month = match.groupValues[2].toIntOrNull()?.takeIf { it in 1..12 } ?: return ""
    val nextYear = if (month == 12) year + 1 else year
    val nextMonth = if (month == 12) 1 else month + 1
    return "01.${nextMonth.toString().padStart(2, '0')}.$nextYear"
}
